import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { LoadingController, NavParams, ToastController } from "ionic-angular";
import * as _ from "lodash";
import LookupValue from "models/patient-chart/lookupValue";
import { PatientChartService } from "providers/patient-charts/patient-chart.service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { CareType } from "../../../models/patient-chart/care-type";
import { StateManagementService } from "../../../providers/state-management/state-management.service";

interface PatientChartData {
  riskPriority?: { id?: string };
  doNotResuscitateStatus?: { id?: string };
  livingWill?: boolean;
  emergencyPreparedness?: string;
}
@Component({
  selector: "patient-care-directives",
  templateUrl: "patient-care-directives.html",
})
export class PatientCareDirectivesPage {
  @Input() lookUpValues: any[] = [];
  @Input() patientDataConsents: any[] = [];
  @Input() patientChartDetailsId: number;
  @Input() patientChartsData: PatientChartData = null;
  @Output() statusDirective: EventEmitter<string> = new EventEmitter<string>();
  patientCareDirectivesFormGroup: FormGroup;
  orderEntryToggle: boolean = false;
  riskPriorities: LookupValue[];
  codeStatuses: LookupValue[];

  spinner = null;
  spinnerCounter = 0;
  isFormDirty: boolean = false;
  originalFormValues: any;
  maxLength: number = 0;
  currentLength: number = this.maxLength;
  isHospice: boolean = true;

  constructor(
    public navParams: NavParams,
    public patientChartService: PatientChartService,
    private fb: FormBuilder,
    public referralService: ReferralsServiceProvider,
    public loadingCtrl: LoadingController,
    public stateData: StateManagementService,
    private toastCtrl: ToastController
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.setLookUpValues(this.lookUpValues);
    this.loadOrderEntryToggle();

    if (
      (this.patientDataConsents && this.patientDataConsents.length > 0) ||
      this.patientChartsData
    ) {
      this.amendForm();
    }
    this.patientCareDirectivesFormGroup.valueChanges.subscribe(() => {
      this.isFormDirty = this.patientCareDirectivesFormGroup.dirty;
    });
    this.isHospice =
      this.patientChartsData["careType"] == "Hospice" ? true : false;
  }

  amendForm() {
    const patientData = this.patientChartsData;
    const formValues = {
      riskPriority:
        patientData.riskPriority && patientData.riskPriority.id
          ? patientData.riskPriority.id
          : null,
      codeStatus:
        patientData.doNotResuscitateStatus &&
        patientData.doNotResuscitateStatus.id
          ? patientData.doNotResuscitateStatus.id
          : null,
      livingWill:
        patientData.livingWill != null ? patientData.livingWill : null,
      emergencyPreparedness: patientData.emergencyPreparedness || "",
      releaseOfInformation: this.getConsentObtained("ROI_CONSENT"),
      allowDataPublicationsForCAHPSReporting:
        this.getConsentObtained("PUBLICATION_CAHPS"),
      hospiceElectionForm: this.getConsentObtained("HOSPICE_ELECTION_FORM"),
      healthCareProxy: this.getConsentObtained("HEALTH_CARE_PROXY"),
      acknowledgementOfCare: this.getConsentObtained("ACKNOWLEDGMENT_OF_CARE"),
      financialPowerOfAttorney: this.getConsentObtained(
        "FINANCIAL_POWER_OF_ATTORNEY"
      ),
      durablePowerOfAttorney: this.getConsentObtained(
        "DURABLE_POWER_OF_ATTORNEY"
      ),
      providerReferralOrders: this.getConsentObtained(
        "PROVIDER_REFERRAL_ORDERS"
      ),
      allRecordsObtained: this.getConsentObtained("ALL_RECORDS"),
    };

    this.patientCareDirectivesFormGroup.setValue(formValues, {
      emitEvent: false,
    });
    this.patientCareDirectivesFormGroup.markAsPristine();
    this.originalFormValues = _.cloneDeep(
      this.patientCareDirectivesFormGroup.value
    );
  }

  hasFormChanged(): boolean {
    return !_.isEqual(
      this.originalFormValues,
      this.patientCareDirectivesFormGroup.value
    );
  }

  private getConsentObtained(type: string): boolean {
    const consent = this.patientDataConsents.find((c) => c.type === type);
    return consent ? consent.obtained : null;
  }

  onSubmit() {
    if (this.patientCareDirectivesFormGroup.valid) {
      const formValues = this.patientCareDirectivesFormGroup.value;
      const clonedPatientCareDirectives =
        this.clonePatientCareDirectivesFromForm(formValues);
      this.increaseSpinnerCount("Loading...");
      this.patientChartService
        .newPatientCareDirectives(
          this.patientChartDetailsId,
          clonedPatientCareDirectives,
          this.orderEntryToggle
        )
        .subscribe(
          (response: any) => {
            this.presentToast("Patient care directives saved successfully");

            this.sendValue("complete");
            this.decreaseSpinnerCount();
            this.originalFormValues = _.cloneDeep(
              this.patientCareDirectivesFormGroup.value
            );
          },
          (err) => {
            this.decreaseSpinnerCount();
            this.referralService.presentToast("Error saving patient");
          }
        ),
        (err) => {
          this.decreaseSpinnerCount();
          this.referralService.presentToast("Error saving patient");
        };
    } else {
      console.log(this.patientCareDirectivesFormGroup.errors);
    }
  }

  sendValue(status: string) {
    this.statusDirective.emit(status);
  }

  onDismiss() {
    if (
      (this.patientDataConsents && this.patientDataConsents.length > 0) ||
      this.patientChartsData
    ) {
      this.amendForm();
      return;
    }
    this.patientCareDirectivesFormGroup.reset();
  }

  private initializeForm() {
    this.patientCareDirectivesFormGroup = this.fb.group({
      riskPriority: [null],
      codeStatus: [null],
      providerReferralOrders: [null],
      hospiceElectionForm: [null],
      livingWill: [null],
      durablePowerOfAttorney: [null],
      acknowledgementOfCare: [null],
      releaseOfInformation: [null],
      healthCareProxy: [null],
      financialPowerOfAttorney: [null],
      allowDataPublicationsForCAHPSReporting: [null],
      allRecordsObtained: [null],
      emergencyPreparedness: [null],
    });
  }

  setLookUpValues(lookUpValues) {
    if (lookUpValues) {
      this.riskPriorities = lookUpValues["RiskPriority"]?.filter(
        (priority) => priority.isDefaultVisible ?? true
      );
      this.codeStatuses = lookUpValues["DoNotResuscitateStatus"];
    }
  }
  clonePatientCareDirectivesFromForm(clone: any) {
    const formatLookupValue = (id: any) => ({
      id: id || null,
      description: "",
      recertificationInterval: "",
    });

    const formatConsentTypes = (formValues: any) => {
      const { isHospice } = formValues;

      const consentTypes = [
        { type: "ROI_CONSENT", obtained: formValues.releaseOfInformation },
        {
          type: "PUBLICATION_CAHPS",
          obtained: formValues.allowDataPublicationsForCAHPSReporting,
        },
        {
          type: "HOSPICE_ELECTION_FORM",
          obtained: formValues.hospiceElectionForm,
        },
        {
          type: "ACKNOWLEDGMENT_OF_CARE",
          obtained: formValues.acknowledgementOfCare,
        },
        {
          type: "PROVIDER_REFERRAL_ORDERS",
          obtained: formValues.providerReferralOrders,
        },
        { type: "HEALTH_CARE_PROXY", obtained: formValues.healthCareProxy },
        {
          type: "FINANCIAL_POWER_OF_ATTORNEY",
          obtained: formValues.financialPowerOfAttorney,
        },
        {
          type: "DURABLE_POWER_OF_ATTORNEY",
          obtained: formValues.durablePowerOfAttorney,
        },
        {
          type: "ALL_RECORDS",
          obtained: formValues.allRecordsObtained,
        },
      ];

      return consentTypes.filter((consent) => consent.obtained !== null);
    };

    const formattedConsentTypes = formatConsentTypes(clone);
    const careType = this.isHospice ? CareType.Hospice : CareType.Palliative;

    return {
      consentTypes: formattedConsentTypes,
      documentsCompleted: ReferralsServiceProvider.calculateDocumentCompletion(
        careType,
        formattedConsentTypes
      ),
      riskPriority: formatLookupValue(clone.riskPriority),
      doNotResuscitateStatus: formatLookupValue(clone.codeStatus),
      emergencyPreparedness: clone.emergencyPreparedness || "",
      ...(clone.livingWill !== null && { livingWill: clone.livingWill }),
    };
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }
  private loadOrderEntryToggle(): void {
    this.stateData.showOrderEntryOM.subscribe((x) => {
      if (x !== null) {
        this.orderEntryToggle = x;
      }
    });
  }
  presentToast(message: string) {
    const toast = this.toastCtrl.create({
      message,
      duration: 3000,
      position: "below",
      cssClass: "success",
    });
    toast.present();
  }

  updateCharacterCount(event: any): void {
    this.currentLength = event.target.value.length;
  }

  minRequiredFieldsObtained(): boolean {
    const BASE_REQUIRED_FIELDS = ["releaseOfInformation", "allRecordsObtained"];
    const HOSPICE_REQUIRED_FIELDS = ["allowDataPublicationsForCAHPSReporting"];

    const minRequiredFields = this.isHospice
      ? [...BASE_REQUIRED_FIELDS, ...HOSPICE_REQUIRED_FIELDS]
      : BASE_REQUIRED_FIELDS;

    return minRequiredFields.every(
      (field) => this.patientCareDirectivesFormGroup.get(field)?.value !== null
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/intake-queue/patient-care-directives/patient-care-directives.ts